import { debounce } from 'lodash';
import React, { useContext, useState, useEffect } from 'react';
import { RicosContext } from 'ricos-context';
import { getHost } from 'wix-rich-content-common';
import HtmlComponent from 'wix-rich-content-plugin-html/libs/HtmlComponent';
import { Image } from 'wix-rich-content-ui-components';
import { LINK_PREVIEW_TYPE } from '../types.js';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".XZ1li,.glob_fontElementMap{font-family:var(--ricos-font-family,unset)}.LZ9JX{display:flex;flex-direction:row;max-height:170px;width:100%}[dir] .LZ9JX{background-color:var(--ricos-background-color,unset);border:1px solid rgba(var(--ricos-text-color-tuple,33,33,33),.2);cursor:pointer;margin:0}[dir] .LZ9JX:hover{border-color:rgba(var(--ricos-text-color-tuple,33,33,33),.6)}@supports not (contain:inline-size){@media only screen and (max-width:640px){.LZ9JX{color:var(--ricos-native-text-color,var(--ricos-text-color,#212121))}[dir] .LZ9JX{border-radius:var(--ricos-native-whitebox-border-radius,0)}}}@container (width < 640px){.LZ9JX{color:var(--ricos-native-text-color,var(--ricos-text-color,#212121))}[dir] .LZ9JX{border-radius:var(--ricos-native-whitebox-border-radius,0)}}.G6Y0S{flex:0 0 auto}[dir] .G6Y0S{background-position:50%;background-repeat:no-repeat;background-size:cover;border-color:var(--ricos-text-color,#212121)}[dir=ltr] .G6Y0S{border-right:1px solid #ededed}[dir=rtl] .G6Y0S{border-left:1px solid #ededed}.Y5OMY{color:var(--ricos-settings-text-color,#000);display:flex;flex-direction:column;font-family:var(--ricos-custom-p-font-family);overflow:hidden;vertical-align:middle;width:100%}[dir] .Y5OMY{padding:20px 28px}.QICLE{align-self:flex-start;color:var(--ricos-text-color,#212121);display:flex;font-family:var(--ricos-custom-h2-font-family);font-size:20px;font-weight:700;line-height:28px;max-height:68px;min-height:26px}[dir] .QICLE{padding-top:12px}.ke8qY{align-self:flex-end}.QICLE,.bZTXF,.zUtAx{overflow:hidden;text-overflow:ellipsis}.bZTXF,.zUtAx{font-size:16px;line-height:24px;white-space:nowrap}.zUtAx{color:var(--ricos-text-color-low,#646464)}[dir=ltr] .zUtAx{text-align:left}[dir=rtl] .zUtAx{text-align:right}.bZTXF{color:var(--ricos-text-color,#212121)}[dir] .bZTXF{padding-top:8px}@supports not (contain:inline-size){@media only screen and (max-width:800px){.LZ9JX{max-height:144px}.G6Y0S{width:110px}[dir] .Y5OMY{padding:12px}.QICLE{font-size:14px;line-height:20px;max-height:48px}[dir] .QICLE{padding-top:8px}.ke8qY{align-self:flex-end}[dir=ltr] .ke8qY{padding-right:12px}[dir=rtl] .ke8qY{padding-left:12px}.zUtAx{line-height:16px}.bZTXF,.zUtAx{font-size:12px}.bZTXF{display:flex;line-height:18px;max-height:44px;white-space:normal}}}@container (width < 800px){.LZ9JX{max-height:144px}.G6Y0S{width:110px}[dir] .Y5OMY{padding:12px}.QICLE{font-size:14px;line-height:20px;max-height:48px}[dir] .QICLE{padding-top:8px}.ke8qY{align-self:flex-end}[dir=ltr] .ke8qY{padding-right:12px}[dir=rtl] .ke8qY{padding-left:12px}.zUtAx{line-height:16px}.bZTXF,.zUtAx{font-size:12px}.bZTXF{display:flex;line-height:18px;max-height:44px;white-space:normal}}";
var styles = {"fontElementMap":"XZ1li","linkPreview":"LZ9JX","linkPreviewImage":"G6Y0S","linkPreviewInfo":"Y5OMY","linkPreviewTitle":"QICLE","ellipsis":"ke8qY","linkPreviewDescription":"bZTXF","linkPreviewUrl":"zUtAx"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const LinkPreviewViewer = ({ componentData, settings, iframeSandboxDomain, helpers }) => {
  var _a;
  const { enableEmbed = true } = settings;
  return enableEmbed && ((_a = componentData.oembed) == null ? void 0 : _a.html) ? /* @__PURE__ */ React.createElement(
    SocialEmbedViewer,
    {
      componentData,
      settings,
      iframeSandboxDomain
    }
  ) : /* @__PURE__ */ React.createElement(LinkPreviewCard, { componentData, helpers });
};
const SocialEmbedViewer = ({ componentData, settings, iframeSandboxDomain }) => {
  const { theme, isMobile } = useContext(RicosContext);
  const htmlCompProps = {
    componentData: __spreadProps(__spreadValues({}, componentData), {
      html: unescape(componentData.oembed.html)
    }),
    settings,
    theme,
    isMobile,
    iframeSandboxDomain
  };
  return /* @__PURE__ */ React.createElement(HtmlComponent, __spreadValues({}, htmlCompProps));
};
const LinkPreviewCard = ({ componentData, helpers }) => {
  var _a;
  const [imageHeight, setImageHeight] = useState(0);
  const [elementRef, setElementRef] = useState(null);
  const { isMobile } = useContext(RicosContext);
  const linkPreviewStyles = styles;
  useEffect(() => {
    if (!elementRef || !(window == null ? void 0 : window.ResizeObserver)) {
      return;
    }
    const resizer = new ResizeObserver(
      debounce((entries) => {
        setImageHeight(Math.round(entries[0].contentRect.height));
      }, 60)
    );
    resizer.observe(elementRef);
    return () => {
      resizer.unobserve(elementRef);
    };
  }, [elementRef]);
  const getUrlForDisplay = (url) => url.replace(/^https?:\/\//, "");
  const onLinkPreviewClick = () => {
    var _a2;
    return (_a2 = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a2.call(helpers, LINK_PREVIEW_TYPE, "Click", componentData.link.url || "");
  };
  const { title, description, thumbnailUrl } = componentData;
  const {
    linkPreview,
    linkPreviewUrl,
    linkPreviewInfo,
    linkPreviewTitle,
    linkPreviewImage,
    linkPreviewDescription
  } = linkPreviewStyles;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    /* @__PURE__ */ React.createElement(
      "figure",
      {
        className: linkPreview,
        "data-hook": "linkPreviewViewer",
        onClick: onLinkPreviewClick,
        ref: setElementRef
      },
      thumbnailUrl && /* @__PURE__ */ React.createElement(
        Image,
        {
          uri: thumbnailUrl,
          containerProps: {
            style: {
              height: imageHeight,
              width: isMobile ? 110 : imageHeight
            },
            className: linkPreviewImage
          },
          height: imageHeight,
          width: isMobile ? 110 : imageHeight,
          displayMode: "fill",
          alt: title
        }
      ),
      /* @__PURE__ */ React.createElement("section", { className: linkPreviewInfo }, /* @__PURE__ */ React.createElement("div", { className: linkPreviewUrl }, getUrlForDisplay(getHost(componentData.link.url) || ((_a = componentData.link) == null ? void 0 : _a.url))), /* @__PURE__ */ React.createElement("figcaption", { className: linkPreviewTitle }, title), description && /* @__PURE__ */ React.createElement("div", { className: linkPreviewDescription }, description))
    )
  );
};

export { LinkPreviewCard, LinkPreviewViewer, SocialEmbedViewer };
//# sourceMappingURL=link-preview-viewer.js.map
